import React, { createContext, useContext, useState, ReactNode } from "react";
import { Source, SourceType, SourceTypeValues } from "../types/frontend";

/**
 * Interface defining the structure of the source context.
 * This context is used to manage and provide source-related data and functions
 * throughout the application.
 */
interface SourceContextType {
  sources: Source[]; // Array of all sources available in the context
  invertedSources: Source[]; // Array of sources in inverted order for display purposes
  addSources: (newSources: Source[]) => void; // Function to add new sources to the existing list
  prependSources: (newSources: Source[]) => void; // Function to prepend new sources to the inverted list
  clearSources: () => void; // Function to clear all sources from the context
  sourceType: SourceType; // The current type of source being used
  setSourceType: (type: SourceType) => void; // Function to set the current source type
  attachedSources: Source[]; // Array of sources currently attached to the chat
  setAttachedSources: React.Dispatch<React.SetStateAction<Source[]>>; // Function to set the attached sources
  clearAttachedSources: () => void; // Function to clear all attached sources
}

/**
 * Create context with undefined default value.
 * This context will be used to provide and consume source-related data and functions
 * throughout the application.
 */
const SourceContext = createContext<SourceContextType | undefined>(undefined);

/**
 * Props for the SourceProvider component
 * @interface SourceProviderProps
 * @property {ReactNode} children - Children to wrap with source context
 */
interface SourceProviderProps {
  children: ReactNode;
}

/**
 * SourceProvider component
 * Provides source state and update functions to child components
 *
 * @param {SourceProviderProps} props
 * @returns {React.ReactElement}
 */
export const SourceProvider: React.FC<SourceProviderProps> = ({ children }) => {
  const [sources, setSources] = useState<Source[]>([]);
  // We use the inverted sources to display the sources in the sources panel in the
  // order of most recent first.
  const [invertedSources, setInvertedSources] = useState<Source[]>([]);
  const [sourceType, setSourceType] = useState<SourceType>(
    SourceTypeValues.ScientificSource
  );

  // Attached sources are the sources that are currently attached to the chat
  const [attachedSources, setAttachedSources] = useState<Source[]>([]);

  /**
   * Appends new sources to the current source list (not inverted)
   * @param {Source[]} newSources
   */
  const addSources = (newSources: Source[]) => {
    setSources((prev) => [...prev, ...newSources]);
  };

  /**
   * Prepends new sources to the current inverted source list
   * @param {Source[]} newSources
   */
  const prependSources = (newSources: Source[]) => {
    setInvertedSources((prev) => [...newSources, ...prev]);
  };

  /**
   * Clears the source list (e.g. when starting new chat)
   */
  const clearSources = () => {
    setSources([]);
    setInvertedSources([]);
  };

  /**
   * Clears the attached sources (e.g. when starting new chat)
   */
  const clearAttachedSources = () => {
    setAttachedSources([]);
  };

  return (
    <SourceContext.Provider
      value={{
        sources,
        invertedSources,
        prependSources,
        addSources,
        clearSources,
        sourceType,
        setSourceType,
        attachedSources,
        setAttachedSources,
        clearAttachedSources,
      }}
    >
      {children}
    </SourceContext.Provider>
  );
};

/**
 * Custom hook to access the source context
 * @throws {Error} If used outside of SourceProvider
 * @returns {SourceContextType} Source context value
 */
export const useSourceContext = (): SourceContextType => {
  const context = useContext(SourceContext);
  if (context === undefined) {
    throw new Error("useSourceContext must be used within a SourceProvider");
  }
  return context;
};
